import { AuthManagerBase, ErrorHandlerResult } from "./managerBase";
import { conferenceLocalStorage, convertKeysToCamelCase } from "../tools";
import { Profile, SigninResponse, UserManager } from "oidc-client";
import { ApolloClient, NextLink, Operation } from "@apollo/client";
import { BackendErrorCode, TStore } from "../types";
import { ExpiredAccessTokenHandler } from "./expiredAccessTokenHandler";

type PCKESignInResponse = SigninResponse & { refresh_token: string };

type TokenData = {
    refresh_token: string;
    access_token: string;
};

type TokenResponse = {
    error?: string;
    error_description?: string;
} & Partial<TokenData>;

function storeToken(token: TokenData) {
    conferenceLocalStorage.accessToken = token.access_token;
    conferenceLocalStorage.refreshToken = token.refresh_token;
}

export class HeimdallManager extends AuthManagerBase {
    private readonly expiredAccessTokenHandler: ExpiredAccessTokenHandler;

    constructor(apolloClient: ApolloClient<any>, store: TStore) {
        super(apolloClient, store);
        this.expiredAccessTokenHandler = new ExpiredAccessTokenHandler(this);
    }

    handleGraphQLError(code: BackendErrorCode, operation: Operation, forward: NextLink): ErrorHandlerResult {
        switch (code) {
            case "expired_access_token": {
                const retry = this.expiredAccessTokenHandler.renewToken(operation, forward);
                // Without a retry the error has to be reported, the token cannot be renewed again.
                return retry ? { return: retry, handled: true } : {};
            }
            case "unknown_access_token":
                this.clearAndReload();
                return { handled: true };
            case "not_authorized":
            case "unreachable":
            case "cant_reach_heimdall":
            case "unknown_invitation":
            case "unknown":
                return {};
            default:
                // the switch is exhaustive, this only guards against a code that is not in the union at runtime
                return {};
        }
    }

    get isLoggedIn(): boolean {
        return !!conferenceLocalStorage.accessToken;
    }

    get authHeader(): string {
        return `Bearer ${conferenceLocalStorage.accessToken}`;
    }

    get userName(): string {
        return conferenceLocalStorage.profile?.name ?? "";
    }

    async login() {
        const client = new UserManager(window.oidcClientSettings);

        const loc = window.location;

        const path = `${loc.protocol}//${loc.host}${loc.pathname}`;

        if (path === window.oidcClientSettings.redirect_uri) {
            const response = (await client.processSigninResponse()) as PCKESignInResponse;

            storeToken(response);
            conferenceLocalStorage.profile = convertKeysToCamelCase(response.profile) as Profile;
            window.location.href = conferenceLocalStorage.beforeLoginUrl;
        } else {
            conferenceLocalStorage.beforeLoginUrl = window.location.href;
            await client.signinRedirect();
        }
    }

    clear() {
        conferenceLocalStorage.accessToken = null;
        conferenceLocalStorage.refreshToken = null;
        conferenceLocalStorage.profile = null;
        conferenceLocalStorage.beforeLoginUrl = null;
    }

    clearAndReload() {
        this.clear();
        window.location.reload();
    }

    async refreshToken(): Promise<void> {
        if (!conferenceLocalStorage.refreshToken) {
            console.debug("No refresh token. Start sign-in with reloading");
            window.location.reload();
        }

        console.debug("Refreshing the access token");

        const data = new FormData();

        data.append("grant_type", "refresh_token");
        data.append("refresh_token", conferenceLocalStorage.refreshToken);
        data.append("client_id", window.oidcClientSettings.client_id);

        const tokenUrl = window.oidcClientSettings.authority + "/token";

        // TODO: handle network errors

        const response = await fetch(tokenUrl, {
            method: "post",
            mode: "cors",
            body: data,
        });

        const tokenResponseData: TokenResponse = await response.json();

        if (tokenResponseData.error) throw Error(tokenResponseData.error);

        storeToken(tokenResponseData as TokenData);
    }
}
